import { faPowerOff, faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { FC, ReactNode, useEffect, useState} from "react";
import { MONITORING_COMMAND, MONITORING_COMMAND_STATE } from '../../constants/command-status';
import { useLocale } from "../../contexts/localeContext";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { useIcaWebsocketContext } from "../../contexts/websocket/IcaWebsocketContext";
import hardwareIcon from '../../images/Hardware.svg';
import AvatarMenu from "../avatar-menu/avatar-menu";
import AvatarMenuPanel from "../avatar-menu/avatar-menu-panel";
import HelpTopicsModal from "../help-topics-modal/help-topics-modal";
import footerLogoIcon from '../../images/CompanyLogo.png';
import EngineStatusModal from "../status/engine-status/engine-status-modal";
import { useSelector } from 'react-redux';
import { IAppState } from '../../store/types';
import { ROLES } from '../../constants/preset-constants';
import ControlPanel from '../dashboard/control-panel/control-panel';
import { useLocation } from 'react-router-dom';

type Props = {
    left?: ReactNode;
    right?: ReactNode;
};

const MenuLayout: FC<Props> = ({ left, right}) => {
    const { translate } = useLocale();
    const icaWebSocket = useIcaWebsocketContext();
    const TYPES = { HELP: 1, ENGINE_STATUS: 2 }
    const [showHelpModal, setShowHelpModal] = useState(false);
    const [showEngineStateModal, setShowEngineStateModal] = useState(false);
    const [showAvatarPanel, setShowAvatarPanel] = useState(false);
    const user = useSelector((state: IAppState) => (state.userInfo));
    const {pathname} = useLocation();

    const showModal = (type) => {
        setShowHelpModal(type == TYPES.HELP);
        setShowEngineStateModal(type == TYPES.ENGINE_STATUS);
    }

    useEffect(() => {
        if (icaWebSocket.connectionState) {
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_ENGINE, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_START);
            /**
                * MenuLayout is not a page component and it's a global component which is always rendered.
                * So, we don't need to set the websocket onMessage event here as this will swallow all the websocket messages.
                * The logic of handling menu layout related messages is on wesocket context provider.
             */
        }
    }, [icaWebSocket.connectionState])

    return (
        <div className="menu-layout flex flex-row w-full h-full">
            <div>{left}</div>
            <div className="flex flex-col w-full overflow-y-hidden bg-darker">
                {showHelpModal ? <HelpTopicsModal setShowHelpModal={setShowHelpModal} /> : null}
                {showEngineStateModal ? <EngineStatusModal setShowModal={setShowEngineStateModal} /> : null}
                {showAvatarPanel ? <AvatarMenuPanel onClose={() => setShowAvatarPanel(false)} /> : null}

                {/* Dashboard action bar: fixed, rendered BEFORE avatar bar so avatar paints on top */}
                {pathname === '/access-control/monitoring/dashboard' &&
                    <div id="dashboardActionBar" style={{ position: 'fixed', top: '6.5rem', right: 0, zIndex: 60 }}>
                        <ControlPanel/>
                    </div>
                }
                <div className="flex flex-row flex-grow justify-between overflow-hidden">
                    <div className="flex flex-col flex-grow overflow-y-auto relative z-[50]">
                        {/* Avatar, help and engine status float top-right, visually inline with breadcrumb */}
                        <div className="fixed top-0 right-0 flex flex-row items-start gap-0 z-[200]">
                            {user.profileId === ROLES.ID_SYSTEM_OWNER || user.profileId === ROLES.ID_SITE_ADMIN ?
                                <button title={translate.byKey(TranslationKeys.engineStatus)} style={{ padding: '12px' }} className="statusBarItemWithBadge" onClick={() => showModal(TYPES.ENGINE_STATUS)} id='engineStatusButton'>
                                    <img style={{ color: 'grey' }} className="statusBarBtn" src={hardwareIcon} alt="" />
                                    <div className="statusBarBadgeIcon" style={{ width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: (icaWebSocket.engineState.isStarted ? '#99BB32' : 'rgb(210, 34, 42)') }}>
                                        <FontAwesomeIcon style={{ height: '0.75rem' }} className='faIcon' icon={faPowerOff} />
                                    </div>
                                </button> : null
                            }
                            <button title={translate.byKey(TranslationKeys.helpTopics)} className="statusBarItem" onClick={() => showModal(TYPES.HELP)} id='helpIcon'>
                                <FontAwesomeIcon style={{ color: 'rgb(210,34,42)', height: '2rem', marginTop: '5px' }} className='faIcon' icon={faQuestionCircle} />
                            </button>
                            <AvatarMenu onOpen={() => setShowAvatarPanel(!showAvatarPanel)} />
                        </div>
                        {right}
                    </div>
                    {/* Full-height grey background column for dashboard */}
                    {pathname === '/access-control/monitoring/dashboard' &&
                        <div id="dashboardSidebar" className="flex-shrink-0 bg-sidemenu-primary" style={{ width: '3.4rem', backgroundColor: 'rgb(242 242 242 / var(--tw-bg-opacity, 1))' }} />
                    }
                </div>
            </div>
        </div>
    );
};

export default MenuLayout;
